import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@material-ui/lab';
import {
  useUserClassroomMoves,
  userClassroomMovesGenerate,
  userClassroomMovesWarmup
} from 'sdk';

import { ReactComponent as FocusIcon } from 'pages/Teachers/Home/assets/focus-icon.svg';
import { ReactComponent as FocusSmallIcon } from 'pages/Teachers/Home/assets/focus-small-icon.svg';
import { ReactComponent as RevisitIcon } from 'pages/Teachers/Home/assets/revisit-icon.svg';
import ColumnContainer from 'pages/Teachers/Home/components/ColumnContainer';
import StartEmptyBox from 'pages/Teachers/Home/components/StartEmptyBox';
import { formatRelativeTime } from 'utils/date';

import styles from './styles.module.scss';

const TomorrowsFocus = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tick, setTick] = useState(0); // state to trigger re-render every minute

  const [startPolling, setStartPolling] = useState(false);

  const {
    data: userClassroomMovesData,
    isLoading: isUserClassroomMovesLoading,
    refetch: refetchUserClassroomMoves
  } = useUserClassroomMoves();

  const tenMinutesAfterLastFetch = useMemo(() => {
    if (tick === 0) return false;
    const lastFetchTime = userClassroomMovesData?.created_at;
    if (isUserClassroomMovesLoading) return false;
    if (!lastFetchTime) return true;

    const tenMinutes = 12 * 60 * 1000;
    return (
      new Date().getTime() - new Date(lastFetchTime).getTime() > tenMinutes
    );
  }, [userClassroomMovesData, isUserClassroomMovesLoading, tick]);

  const fetchFocusData = useCallback(async () => {
    setIsLoading(true);
    try {
      await userClassroomMovesGenerate();
      refetchUserClassroomMoves();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [refetchUserClassroomMoves]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (startPolling) {
      const interval = setInterval(() => {
        refetchUserClassroomMoves();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [startPolling, refetchUserClassroomMoves]);

  useEffect(() => {
    if (
      tenMinutesAfterLastFetch === true &&
      userClassroomMovesData?.generation_in_progress === false
    ) {
      fetchFocusData();
    } else if (
      userClassroomMovesData?.has_insights_generation_error === true &&
      userClassroomMovesData?.generation_in_progress === false
    ) {
      fetchFocusData();
    }
  }, [
    tenMinutesAfterLastFetch,
    userClassroomMovesData?.generation_in_progress,
    fetchFocusData,
    userClassroomMovesData?.has_insights_generation_error
  ]);

  useEffect(() => {
    userClassroomMovesWarmup();
  }, []);

  useEffect(() => {
    if (userClassroomMovesData?.generation_in_progress === true) {
      setStartPolling(true);
    } else {
      setStartPolling(false);
    }
  }, [userClassroomMovesData?.generation_in_progress]);

  return (
    <ColumnContainer
      title=""
      titleDescription="Tomorrow's Focus"
      smallIcon={<FocusSmallIcon />}
      isCommingSoon={false}
      headerRight={formatRelativeTime({
        date: userClassroomMovesData?.created_at
      })}
    >
      <div className={styles.focusWidget}>
        {(isLoading || isUserClassroomMovesLoading) && (
          <div className={styles.loadingState}>
            <Skeleton variant="rect" width="100%" height={180} />
          </div>
        )}
        {!isLoading &&
          !isUserClassroomMovesLoading &&
          userClassroomMovesData?.suggested_moves.length > 0 && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <RevisitIcon className={styles.warningIcon} />
                <span className={styles.headerTitle}>Revisit</span>
              </div>

              <ul className={styles.bulletList}>
                {userClassroomMovesData?.suggested_moves.map(
                  (bullet, index) => (
                    <li key={index} className={styles.bulletItem}>
                      {bullet}
                    </li>
                  )
                )}
              </ul>
            </div>
          )}
        {!isLoading &&
          !isUserClassroomMovesLoading &&
          userClassroomMovesData.length === 0 &&
          !userClassroomMovesData.has_insights_generation_error && (
            <StartEmptyBox
              stepNumber="STEP 2"
              description="Reflect on today's data and set goals for your next lesson."
              icon={<FocusIcon />}
            />
          )}

        {!isLoading &&
          !isUserClassroomMovesLoading &&
          userClassroomMovesData.length === 0 &&
          userClassroomMovesData?.has_insights_generation_error && (
            <div className={styles.errorState}>
              We encountered an issue while generating insights for your
              classroom moves. Please try again later.
            </div>
          )}
      </div>
    </ColumnContainer>
  );
};

export default TomorrowsFocus;
